/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-constant-condition */

import { Box, Stack, Typography, FormControl, InputLabel, MenuItem } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  // useNavigate,
  useSearchParams
} from "react-router-dom";
import * as yup from "yup";

import { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { ICompanyRegisterForm, IGenericResponse } from "../types/forms";
import api from "../services/api";
import Translate from "../components/Translate";
import useAppTranslations from "../hooks/useAppTranslations";
import useYupFormResolver from "../hooks/useYupFormResolver";
import FormTextField from "../components/FormTextField";
import Landing from "../components/Landing";
import regex from "../helpers/regex";
import { cpfOrCnpjMask } from "../helpers/mask";
import { validateCnpj } from "../helpers/validations";
import { IBasePlan } from "../models";
import SelectInput from "../components/SelectInput";
import useCustomSnackBar from "../hooks/useCustomSnackBar";
import partners from "../utils/partners";

const formInitialState: ICompanyRegisterForm = {
  companyCnpj: cpfOrCnpjMask.maskDefault(""),
  companyName: "",
  userEmail: "",
  userName: "",
  planId: 0,
  affiliationCode: null
};

export default function CompanyRegisterPage() {
  const { t } = useAppTranslations();
  const [plan, setPlan] = useState("1");
  const [plans, setPlans] = useState<IBasePlan[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const planName = searchParams.get("plan");
  const partnerName = searchParams.get("partner");
  const { toastMessage } = useCustomSnackBar();

  const yupFormValidation = yup.object({}).shape({
    companyCnpj: yup
      .string()
      .test("validate_cnpj", t("validation:valid_cnpj"), x => validateCnpj(x ?? ""))
      .matches(regex.CPF_OR_CNPJ, { message: t("validation:valid_cnpj") })
      .required(
        t("validation:required_field", {
          fieldName: t("form:field:cnpj")
        })
      ),

    companyName: yup.string().required(
      t("validation:required_field", {
        fieldName: t("form:field:company_name")
      })
    ),

    userEmail: yup
      .string()
      .required(
        t("validation:required_field", {
          fieldName: t("form:field:corp_email")
        })
      )
      .email(t("validation:valid_email")),

    userName: yup.string().required(
      t("validation:required_field", {
        fieldName: t("form:field:full_name")
      })
    )
  });

  const yupFormResolver = useYupFormResolver<typeof yupFormValidation>(yupFormValidation);

  const formHook = useForm<ICompanyRegisterForm>({
    resolver: yupFormResolver,
    values: formInitialState,
    mode: "onBlur"
  });

  const extractAffiliationCode = () => {
    const params = new URLSearchParams(location.search);
    return params.get("affiliationCode");
  };

  const handleSearchPartner = (partnerParam: string) => {
    if (partnerName) {
      const partnerFiltered = partners.find(
        partner => partner.name.toUpperCase() === partnerParam.toUpperCase()
      );
      return partnerFiltered?.id;
    }
    return null;
  };

  const registerCompany = async (data: ICompanyRegisterForm) => {
    try {
      data.companyCnpj = data.companyCnpj.replaceAll(/[^0-9]/gi, "");
      data.affiliationCode = extractAffiliationCode();
      const response = await api.postRegisterCompany(data);

      if (response.data.success) {
        navigate("/success", { state: { userEmail: data.userEmail } });
      }
    } catch (e: any) {
      const error = e as AxiosError<IGenericResponse<any>>;
      if (error.response) toastMessage(error.response.data.message);
      else toastMessage("Erro de comunicação com o serviço do Register");
    }
  };

  useEffect(() => {
    api.getPlans(handleSearchPartner(partnerName as string)).then(resp => {
      setPlans(resp.data.data);

      if (planName === "trial") {
        setPlan("1");
        return;
      }

      const selectedPlan = resp.data.data.find(
        p => p.name.toLowerCase() === planName?.toLowerCase()
      );
      if (selectedPlan) {
        setPlan(selectedPlan.id.toString());
      }
    });
  }, [planName, partnerName]);

  useEffect(() => {
    formHook.setValue("planId", Number(plan));
  }, [formHook, plan]);

  const handleChangePlan = (event: SelectChangeEvent<unknown>) => {
    setPlan(event.target.value as string);
  };

  return (
    <Landing
      headerTitle={t("title:create_and_start_now")}
      submitButtonLabel={t("action:create_and_start_now")}
      handleCardFormSubmit={formHook.handleSubmit(registerCompany)}
      submitLoading={formHook.formState.isSubmitting}
      CardFormContent={
        <Box
          className="rounded-2xl"
          sx={theme => ({
            py: 3,
            px: 4,
            background: theme.palette.background.default,
            border: `solid 1px ${theme.palette.grey[300]}`,

            [theme.breakpoints.down("sm")]: {
              border: "none",
              py: 1.5,
              px: 2
            }
          })}
        >
          <Stack
            sx={theme => ({
              flexDirection: "row",
              gap: theme.spacing(4),
              marginBottom: "4px",
              [theme.breakpoints.down("sm")]: {
                flexDirection: "column"
              }
            })}
          >
            <Box className="grow">
              <Typography variant="subtitle2">
                <Translate src="form:legend:corp_form" />
              </Typography>

              <FormTextField
                id="companyCnpj"
                disabled={formHook.formState.isSubmitting}
                label={t("form:field:cnpj")}
                placeholder={t("form:placeholder:cnpj")}
                fieldControl={formHook.control}
                fieldName="companyCnpj"
                onChange={cpfOrCnpjMask.onChange}
              />

              <FormTextField
                id="companyName"
                disabled={formHook.formState.isSubmitting}
                label={t("form:field:company_name")}
                placeholder={t("form:placeholder:company_name")}
                fieldControl={formHook.control}
                fieldName="companyName"
              />
            </Box>

            <Box className="grow">
              <Typography variant="subtitle2">
                <Translate src="form:legend:user_form" />
              </Typography>

              <FormTextField
                id="userName"
                disabled={formHook.formState.isSubmitting}
                label={t("form:field:full_name")}
                placeholder={t("form:placeholder:full_name")}
                fieldControl={formHook.control}
                fieldName="userName"
              />

              <FormTextField
                id="userEmail"
                disabled={formHook.formState.isSubmitting}
                label={t("form:field:corp_email")}
                placeholder={t("form:placeholder:corp_email")}
                fieldControl={formHook.control}
                fieldName="userEmail"
              />
            </Box>
          </Stack>
          <FormControl fullWidth>
            <InputLabel id="select-plan-label">Plano</InputLabel>
            <SelectInput
              className="p-0 text-slate-100"
              labelId="select-plan-label"
              id="select-plan"
              value={plan}
              label="Plano"
              disabled={planName?.toLowerCase() === "trial" || formHook.formState.isSubmitting}
              onChange={(event: SelectChangeEvent<unknown>) => handleChangePlan(event)}
            >
              {plans.map(item => {
                return (
                  <MenuItem key={item.id} value={item.id}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1rem"
                      }}
                    >
                      {item.name}
                    </Typography>
                  </MenuItem>
                );
              })}
            </SelectInput>
          </FormControl>
        </Box>
      }
    />
  );
}
